/**
 * RF-07 · Asignación de personas a indicadores
 * (`/api/indicador-evaluadores`).
 *
 * El trabajo se reparte en tres tareas: `evidencias`, `instrumentos` y
 * `calificar`. Una asignación SIN `id_indicador` designa al responsable del
 * proceso, que después reparte los indicadores.
 *
 * No hay actualización: cambiar una asignación es borrarla y crearla, para que el
 * rastro de quién estuvo asignado no se sobrescriba.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type IndicadorEvaluador from '@/interfaces/IndicadorEvaluador'
import { http } from '@/services/http'

const BASE = '/api/indicador-evaluadores'

/**
 * Consulta indicador evaluador · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns IndicadorEvaluador[]
 */
export async function getIndicadorEvaluadoresService(
  params: { id_version?: number; id_indicador?: number; id_usuario?: number; tarea?: string; id_ejecucion?: number }
): Promise<IndicadorEvaluador[]> {
  const query = new URLSearchParams()
  if (params.id_version)   query.set('id_version',   String(params.id_version))
  if (params.id_ejecucion) query.set('id_ejecucion', String(params.id_ejecucion))
  if (params.id_indicador) query.set('id_indicador', String(params.id_indicador))
  if (params.id_usuario)   query.set('id_usuario',   String(params.id_usuario))
  if (params.tarea)        query.set('tarea',         params.tarea)
  const url = query.toString() ? `${BASE}?${query}` : BASE
  const result = await http.get<any>(url)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : d?.data ?? []
}

/**
 * Crea indicador evaluador · petición `POST`.
 *
 * @returns { ok: boolean; data?: IndicadorEvaluador; errors?: Record<string, string[]> }
 */
export async function crearIndicadorEvaluadorService(
  payload: Omit<IndicadorEvaluador, 'id'>
): Promise<{ ok: boolean; data?: IndicadorEvaluador; errors?: Record<string, string[]> }> {
  const result = await http.post<IndicadorEvaluador>(BASE, payload)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true, data: result.data }
}

/**
 * Elimina indicador evaluador · petición `DELETE`.
 *
 * @returns { ok: boolean; errors?: Record<string, string[]> }
 */
export async function eliminarIndicadorEvaluadorService(
  id: number
): Promise<{ ok: boolean; errors?: Record<string, string[]> }> {
  const result = await http.delete(`${BASE}/${id}`)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true }
}
